import React from "react";

import {
  Typography,
  Grid,
  GridProps,
  Backdrop,
  CircularProgress,
  IconButton,
  Button,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";
import AddIcon from "@mui/icons-material/Add";

import { TabLink, TabLinkProps } from "components/MUIExtensions/TabLink";
import { SelectorMonthYear, SelectorMonthYearProps } from "./SelectorMonthYear";
import { SpaceSelector, SpaceSelectorProps } from "./SpaceSelector";
import { ArrowBackIos } from "@mui/icons-material";
import { useHistory } from "react-router-dom";

const useStyles = makeStyles()((theme) => ({
  outer: {
    backgroundColor: theme.palette.common.white,
    padding: theme.spacing(4, 3),
    gridArea: "main",
  },
}));

export const PageStandard = ({
  title,
  tabs,
  children,
  subtitle,
  displaySelector = false,
  displayLoading = false,
  hideDivider = false,
  selectorFilter,
  onNoSpaceAvailable,
  onAdd,
  ...dateProps
}: PageStandardProps) => {
  const { classes } = useStyles();
  const history = useHistory();

  return (
    <Grid container direction="column" className={classes.outer}>
      <Backdrop
        sx={(theme) => ({ zIndex: theme.zIndex.drawer + 1 })}
        open={displayLoading}
      >
        <CircularProgress />
      </Backdrop>

      <Grid item container direction="row" sx={{ mb: 2 }}>
        <Grid item container alignItems="center" sx={{ flex: 1 }}>
          <IconButton size="small" onClick={() => history.goBack()}>
            <ArrowBackIos
              sx={{
                height: (theme) => theme.typography.h3.fontSize,
                lineHeight: (theme) => theme.typography.h1.lineHeight,
              }}
            />
          </IconButton>
          <Typography variant="h1" sx={{ mb: 0 }}>
            {title}
          </Typography>
          {subtitle && <Typography variant="body1">{subtitle} </Typography>}
        </Grid>

        {!!onAdd && (
          <Button
            sx={{ minWidth: 0, width: "48px", height: "48px" }}
            variant="contained"
            onClick={onAdd}
          >
            <AddIcon />
          </Button>
        )}
      </Grid>

      {displaySelector && (
        <SpaceSelector
          filterSpaces={selectorFilter}
          onNoSpaceAvailable={onNoSpaceAvailable}
        />
      )}

      {tabs && <TabLink items={tabs} />}
      {"referenceDate" in dateProps && (
        <SelectorMonthYear
          referenceDate={dateProps.referenceDate}
          onDatechange={dateProps.onDatechange}
          yearOnly={dateProps.yearOnly}
          customDisplayText={dateProps.customDisplayText}
        />
      )}
      {children}
    </Grid>
  );
};

type PageStandardBase = {
  /* Title to be displayed in h1 tag */
  title: string;
  /* Text displayed under the title */
  subtitle?: string;
  /* Content to be rendered under the filter */
  children: GridProps["children"];
  /* Menu tabs to be rendered inside TabLink */
  tabs?: TabLinkProps["items"];
  /* Should the AutomationSpace selector be displayed? Default: false */
  displaySelector?: boolean;
  /* Should the fullsscreen loading overlay be displayed? Default: false */
  displayLoading?: boolean;
  /* Hides divider under title, default = false */
  hideDivider?: boolean;
  selectorFilter?: SpaceSelectorProps["filterSpaces"];
  onNoSpaceAvailable?: SpaceSelectorProps["onNoSpaceAvailable"];
  onAdd?: () => void;
};

type PageStandardDateInfo = PageStandardBase & {
  /** Date with month and year that will be displayed by the component */
  referenceDate: SelectorMonthYearProps["referenceDate"];
  /** Handler for when the user saves the changes */
  onDatechange: SelectorMonthYearProps["onDatechange"];
  /** Only allow user to pick a year */
  yearOnly?: SelectorMonthYearProps["yearOnly"];
  customDisplayText?: SelectorMonthYearProps["customDisplayText"];
};

export type PageStandardProps = PageStandardBase | PageStandardDateInfo;
